/* eslint-disable eqeqeq */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable default-case */
/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';

import { ec as EC } from 'elliptic';
import { serverTimestamp } from 'firebase/firestore';
import {
  Archive,
  ArrowRight,
  BadgeAlert,
  CheckCircle2,
  Copy,
  Fingerprint,
  Key,
  Layers,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { db } from '../config/firebaseConfig';
import { UserAuth } from '../context/authContext';
import FirestoreTimestampToDate from './utils/FirebaseTimetoDate';

const CopyButton = ({ text }) => (
  <button
    onClick={() => {
      navigator.clipboard.writeText(text);
      toast.success('Copied successfully');
    }}
    className="p-1 hover:bg-white/10 rounded transition-colors group ml-1"
    title="Copy to clipboard"
  >
    <Copy
      size={12}
      className="text-white/40 group-hover:text-white/80 transition-colors"
    />
  </button>
);

const MemPool = ({ embedded = false }) => {
  const [roomId, setRoomId] = useState(
    embedded
      ? localStorage.getItem('roomId') || ''
      : localStorage.getItem('roomId') || ''
  );
  const { userData } = UserAuth();
  const [hasAddedTransactions, setHasAddedTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);

  const generatePublicKeyFromPrivate = (privateKey) => {
    const ec = new EC('secp256k1');
    const keyPair = ec.keyFromPrivate(privateKey.slice(2), 'hex');
    return '0x' + keyPair.getPublic(false, 'hex').slice(2);
  };

  const verifyPrivateKey = (senderPrivateKey, senderPublicKey) => {
    const generatedSenderPublicKey =
      generatePublicKeyFromPrivate(senderPrivateKey);
    const normalizedGenerated = generatedSenderPublicKey.trim().toLowerCase();
    const normalizedInput = senderPublicKey.trim().toLowerCase();
    return normalizedGenerated === normalizedInput;
  };

  const verifyTransaction = async (transaction) => {
    if (!roomId) return;

    try {
      const signature = transaction.signature;
      const matchResult = signature.match(/0x[a-fA-F0-9]{64}/g);

      if (!matchResult || matchResult.length < 2) {
        toast.error('Invalid signature format');
        return;
      }

      const [receiverPublicKey, senderPrivateKey] = matchResult;

      const senderPublicKey = prompt(
        "Enter the sender's public key to verify the transaction"
      );
      if (!senderPublicKey) {
        toast.error(
          "Sender's public key is required to verify the transaction"
        );
        return;
      }

      const isValidPrivateKey = verifyPrivateKey(
        senderPrivateKey,
        senderPublicKey
      );

      if (!isValidPrivateKey) {
        toast.error("Invalid sender's public key");
        return;
      } else {
        await db
          .collection('rooms')
          .doc(roomId)
          .collection('transactions')
          .doc(transaction.id)
          .update({
            minerPublicKey: userData.publicKey,
            minerEmail: userData.email,
            status: 'Validate',
            verified: true,
          });
        toast.success("Sender's private key verified successfully");
      }
      fetchRoomTransactions();
    } catch (e) {
      console.error('Failed to vote:', e.message);
      toast.error('Failed to vote');
    }
  };

  const validateTransaction = async (transaction) => {
    if (!roomId) return;

    const senderRef = db.collection('users').doc(transaction.senderEmail);
    const userSnapshot = await senderRef.get();
    const senderData = userSnapshot.data();
    if (!senderData) {
      toast.error('Sender not found');
      return;
    }

    if (userData.publicKey !== transaction.minerPublicKey) {
      toast.error('You are not the verifier of this transaction');
      return;
    }

    try {
      if (Number(senderData.balance) < Number(transaction.amount)) {
        toast.error('Insufficient balance');
        await db
          .collection('rooms')
          .doc(roomId)
          .collection('transactions')
          .doc(transaction.id)
          .update({
            status: 'failed',
            failedMessage: 'Insufficient balance',
            validatedAt: serverTimestamp(),
          });
      } else {
        await db
          .collection('rooms')
          .doc(roomId)
          .collection('transactions')
          .doc(transaction.id)
          .update({
            status: 'Add to block',
            validatedAt: serverTimestamp(),
          });

        const newBalance = senderData.balance - transaction.amount;
        await senderRef.update({
          balance: newBalance,
        });
        toast.success('Validation successful!');
      }
    } catch (e) {
      console.error('Failed to vote:', e.message);
      toast.error('Failed to vote');
    }
  };

  const addTransactionToBlock = async (transaction) => {
    if (!roomId) return;
    if (!transaction.verified) {
      toast.error('Transaction not verified');
      return;
    }
    if (transaction.status === 'Validate') {
      toast.error(
        'Transaction is pending, first verify and validate the transaction'
      );
      return;
    }
    if (transaction.status === 'failed') {
      toast.error('Transaction is failed, cannot be added');
      return;
    }
    if (transaction.blockStatus === 'added') {
      toast.error('Transaction already added to block');
      return;
    }
    if (transaction.minerPublicKey !== userData.publicKey) {
      toast.error('You are not the verifier of this transaction');
      return;
    }

    try {
      await db
        .collection('rooms')
        .doc(roomId)
        .collection('transactions')
        .doc(transaction.id)
        .update({
          blockStatus: 'added',
          status: 'Added to block',
          addedAt: serverTimestamp(),
        });
      toast.success('Transaction added to block successfully');
    } catch (e) {
      console.error('Failed to add transaction to block:', e.message);
      toast.error('Failed to add transaction to block');
    }
  };

  const updateActionState = (transaction) => {
    switch (transaction.status) {
      case 'Verify':
        verifyTransaction(transaction);
        break;
      case 'Validate':
        validateTransaction(transaction);
        break;
      case 'Add to block':
        addTransactionToBlock(transaction);
        break;
    }
    fetchRoomTransactions();
  };

  const fetchRoomTransactions = async () => {
    if (!roomId) return;

    try {
      const roomRef = db.collection('rooms').doc(roomId);
      roomRef
        .collection('transactions')
        .orderBy('timestamp', 'desc')
        .onSnapshot((snapshot) => {
          const data = [];
          snapshot.forEach((t) => {
            data.push({ id: t.id, ...t.data() });
          });
          setTransactions(data);
          const allAdded = data.every(
            (t) =>
              t.status === 'Added to block' || t.status === 'Added to chain'
          );
          setHasAddedTransactions(allAdded);
        });
    } catch (error) {
      console.error('Error fetching room transactions:', error);
      toast.error('Error fetching room transactions.');
    }
  };

  useEffect(() => {
    if (roomId) fetchRoomTransactions();
  }, [roomId]);

  const filteredTransactions = transactions.filter(
    (transaction) =>
      transaction.status !== 'genisis' &&
      transaction.status !== 'Added to chain' &&
      transaction.status !== 'Added to block'
  );

  return (
    <div
      className={embedded ? '' : 'min-h-screen pt-8 pb-12 px-6 bg-[#0B0E14]'}
    >
      <div className={embedded ? '' : 'max-w-[95vw] mx-auto space-y-8'}>
        {/* Header */}
        {!embedded && (
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col gap-2">
              <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                <Layers className="text-[#6C4FFF]" size={32} />
                Transaction Mempool
              </h2>
              <p className="text-white/40">
                Manage and validate pending transactions before they are added
                to blocks.
              </p>
            </div>

            <a
              href="/blocks"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E2330] hover:bg-[#2A3040] border border-white/10 text-white rounded-xl transition-all font-medium group"
            >
              <Archive size={18} className="text-[#6C4FFF]" />
              View Blocks
              <ArrowRight
                size={16}
                className="text-white/40 group-hover:translate-x-1 transition-transform"
              />
            </a>
          </div>
        )}

        {/* Transactions Table */}
        <div className="bg-[#1E2330]/50 backdrop-blur rounded-2xl border border-white/5 overflow-hidden shadow-xl">
          {filteredTransactions.length >= 1 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5 bg-white/[0.02]">
                    <th className="text-left py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                      Tx Details
                    </th>
                    <th className="text-left py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                      Parties
                    </th>
                    <th className="text-left py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                      Verification
                    </th>
                    <th className="text-left py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                      Time
                    </th>
                    <th className="text-right py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="text-center py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-right py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredTransactions.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="py-4 px-6 align-top">
                        <div className="flex flex-col gap-1">
                          <span className="text-white/40 text-[10px] uppercase font-bold">
                            Hash
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-white font-mono text-xs">
                              {transaction.hash?.substring(0, 12)}...
                            </span>
                            <CopyButton text={transaction.hash} />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 align-top">
                        <div className="flex flex-col gap-2">
                          <div>
                            <span className="text-white/40 text-[10px] uppercase font-bold block mb-0.5">
                              From
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="text-white/80 font-mono text-xs">
                                {transaction.sender?.substring(0, 8)}...
                              </span>
                              <CopyButton text={transaction.sender} />
                            </div>
                          </div>
                          <div>
                            <span className="text-white/40 text-[10px] uppercase font-bold block mb-0.5">
                              To
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="text-white/80 font-mono text-xs">
                                {transaction.receiver?.substring(0, 8)}...
                              </span>
                              <CopyButton text={transaction.receiver} />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 align-top">
                        <div className="flex flex-col gap-1">
                          <span className="text-white/40 text-[10px] uppercase font-bold">
                            Signature
                          </span>
                          <div className="flex items-center gap-1 bg-black/20 p-2 rounded border border-white/5">
                            <Fingerprint size={12} className="text-white/40" />
                            <span
                              className="text-white/60 font-mono text-[10px] truncate max-w-[120px]"
                              title={transaction.signature}
                            >
                              {transaction.signature?.substring(0, 16)}...
                            </span>
                            <CopyButton text={transaction.signature} />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-white/60 text-xs align-top pt-8">
                        {FirestoreTimestampToDate(transaction.timestamp)}
                      </td>
                      <td className="py-4 px-6 text-right align-top pt-8">
                        <span className="text-white font-bold">
                          {transaction.amount}
                        </span>
                        <span className="text-white/40 text-xs ml-1">AST</span>
                      </td>
                      <td className="py-4 px-6 align-top pt-6 text-center">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                            transaction.status === 'failed'
                              ? 'bg-red-500/10 text-red-400 border-red-500/20'
                              : transaction.status == 'Verify'
                                ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                : transaction.status == 'Validate'
                                  ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                  : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          }`}
                        >
                          {transaction.status === 'failed' ? (
                            <>
                              <BadgeAlert size={12} /> Failed
                            </>
                          ) : transaction.status == 'Verify' ? (
                            <>
                              {' '}
                              <ShieldAlert size={12} /> Not Verified{' '}
                            </>
                          ) : transaction.status == 'Validate' ? (
                            <>
                              {' '}
                              <Key size={12} /> Verified{' '}
                            </>
                          ) : (
                            <>
                              {' '}
                              <ShieldCheck size={12} /> Validated{' '}
                            </>
                          )}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right align-top pt-6">
                        {transaction.status !== 'Added to block' ? (
                          <button
                            disabled={transaction.status === 'failed'}
                            onClick={() => updateActionState(transaction)}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all shadow-lg ${
                              transaction.status === 'failed'
                                ? 'bg-red-500/50 text-white/50 cursor-not-allowed shadow-none'
                                : transaction.status === 'Verify'
                                  ? 'bg-[#6C4FFF] hover:bg-[#5840D9] text-white shadow-[#6C4FFF]/20'
                                  : transaction.status === 'Validate'
                                    ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/20'
                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/20'
                            }`}
                          >
                            {transaction.status === 'failed' ? (
                              <BadgeAlert size={14} />
                            ) : transaction.status === 'Verify' ? (
                              <ShieldCheck size={14} />
                            ) : transaction.status === 'Validate' ? (
                              <CheckCircle2 size={14} />
                            ) : (
                              <Layers size={14} />
                            )}

                            {transaction.status === 'failed'
                              ? 'Tx Failed'
                              : transaction.status === 'Verify'
                                ? 'Verify Sig'
                                : transaction.status === 'Validate'
                                  ? 'Validate Tx'
                                  : 'Add to Block'}
                          </button>
                        ) : (
                          <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg text-xs font-medium">
                            <CheckCircle2 size={12} /> Added
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
                <BadgeAlert className="text-white/20" size={32} />
              </div>
              <h3 className="text-lg font-medium text-white mb-1">
                Mempool is Empty
              </h3>
              <p className="text-white/40 max-w-sm mx-auto">
                No pending transactions to process at the moment. New
                transactions will appear here for verification.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemPool;
